// Angular
import { Injectable } from '@angular/core';

// NGRX
import { Observable, of } from 'rxjs';
import { Store, select } from '@ngrx/store';
import { filter, map, catchError, take } from 'rxjs/operators';

// Miscellaneous
import * as actions from '../../../actions';
import * as fromRoot from '../../../reducers';
import { State } from '../../../reducers/guest-details';
import * as guestActions from '../../../actions/guest.actions';

// Components
import * as _ from 'lodash';
import { Guest } from '../../../models';
import { constants } from '../../../constants';
import { HomeAddress } from '../../../models/guest-details/home-address';
import { PersonalInfo } from '../../../models/guest-details/personal-info';
import { GuestDetails } from '../../../models/guest-details/guest-details';
import { TravelDocument } from '../../../models/guest-details/travel-document';
import { ContactInformation } from '../../../models/guest-details/contact-information';
import { ImmigrationInformation } from '../../../models/guest-details/immigration-information';
import { EmergencyContactInformation } from '../../../models/guest-details/emergency-contact-information';

// Services
import { GuestService } from './guest.service';
import { BookingService } from './booking.service';
import { MicroServicesService } from './micro-services.service';
import { TravelDocumentsService } from './travel-documents.service';

@Injectable()
export class GuestDetailsService {
	private _state: State;
	private currentGuestId : number;

	constructor(
		private readonly store: Store<fromRoot.AppState>,
		private readonly guestService: GuestService,
		private readonly bookingService: BookingService,
    private readonly microServicesService: MicroServicesService,
    public readonly travelDocumentsService: TravelDocumentsService,
	) {
		this.store.pipe(select(fromRoot.guestDetailsState)).subscribe(state => (this._state = state));
	}

	public selectGuest(guestId?: number, selectedGuests?: Guest[]) {
		const selectedGuestsWithoutProfile: Guest[] = [];

		if (guestId && this._state.guestDetails[guestId]) {
			this.store.dispatch(new actions.SetCurrentGuestIdAction(guestId));
      this.store.dispatch(new actions.UpdateSelectedGuestAction(guestId));
			return;
		}

		this.addItemsToGuestsWithoutProfile(selectedGuests, selectedGuestsWithoutProfile);

		if (!selectedGuestsWithoutProfile.length) {
			return;
		}

		const guestBooking = this.guestService.getAllGuestsBookingCombination(selectedGuestsWithoutProfile);
		const reservationData = this.bookingService.reservationData;
		const payload = {
			shipCode: reservationData?.shipCode,
			sailDate: reservationData?.sailDate,
			source: constants.SOURCE,
			ipAddress: constants.IP_ADDRESS,
			guests: guestBooking
		};
		this.store.dispatch(new actions.LoadBeginDetailsAction(payload));
	}

	public getGuestsDetails(selectedGuests: Guest[]): Observable<any> {
		const selectedGuestsWithoutProfile: Guest[] = [];
		this.addItemsToGuestsWithoutProfile(selectedGuests, selectedGuestsWithoutProfile);
    if (!selectedGuestsWithoutProfile.length) {
			return of(null);
		}

		this.store.dispatch(new actions.SetLoadBeginDetailsAction());
		const guestBooking = this.guestService.getAllGuestsBookingCombination(selectedGuestsWithoutProfile);
		const reservationData = this.bookingService.reservationData;
    const payload = {
      shipCode: reservationData?.shipCode,
      sailDate: reservationData?.sailDate,
      source: constants.SOURCE,
      ipAddress: constants.IP_ADDRESS,
      guests: guestBooking,
      grantor: {
        guestId: reservationData?.guestId,
        bookingId: reservationData?.bookingId
      }
    };

		return this.microServicesService
      .getBeginDetails(payload)
      .pipe(
        map(beginDetailsResponse => {
          if (beginDetailsResponse.status !== constants.HTTP_STATUS_CODE.SUCCESS) {
            //validTravelDocuments
            return of(null);
          }
          const successPayload = Object.assign({}, beginDetailsResponse, {
            loggedInPaxId: this.guestService.loggedInGuestPaxId
          });

          const checkinToken = _.get(beginDetailsResponse, 'payload.checkinToken', '');
          this.store.dispatch(new actions.UpdateCheckinToken(checkinToken));
          this.store.dispatch(new guestActions.UpdateGuestsInfoFromBeginMicroservice(beginDetailsResponse));
          return successPayload;
        }),
        catchError(error => {
          return of(error);
        })
		);
	}

  private addItemsToGuestsWithoutProfile(selectedGuests: Guest[] | undefined, selectedGuestsWithoutProfile: Guest[]) {
    if (selectedGuests && selectedGuests.length) {
      selectedGuests.forEach((guest: Guest) => {
        if (!this._state.guestDetails[guest.guestId]) {
          selectedGuestsWithoutProfile.push(guest);
        }
      });
    }
  }

	public updatedBeginDetailsResponse(
    beginDetailsResponse: any,
    documentTypes: any[]
  ) {
		let beginDetailsResponseUpdated = {};
		let payload = {};

		const bookingsGuestsInformation = _.get(beginDetailsResponse, 'payload.bookings', []);
		if (!bookingsGuestsInformation.length) {
			return of(null);
		} else {
			const bookings: any = [];
			bookingsGuestsInformation.forEach((booking: any) => {
				const guests = booking.guests;
				const updatedGuests: any = [];
				if (!guests.length) {
					return of(null);
				}
				guests.forEach((guest: any) => {
					const selectedGuest = Object.assign({}, guest, {
						validTravelDocuments: this.travelDocumentsService.retrieveValidTravelDocuments(documentTypes, guest.guestId)
					});
					updatedGuests.push(selectedGuest);
				});
				const selectedBooking = Object.assign({}, booking, {
					guests: updatedGuests
				});
				bookings.push(selectedBooking);
			});
			payload = Object.assign({}, beginDetailsResponse.payload, { bookings });
			beginDetailsResponseUpdated = Object.assign({}, beginDetailsResponse, {
				payload: payload
			});
		}
		return beginDetailsResponseUpdated;
	}

	public isStored(guestId: number): boolean {
		return !!this._state.guestDetails[guestId];
	}

	public get personalInfo(): Observable<PersonalInfo> {
		return this.store.pipe(select(fromRoot.selectedGuestPersonalInfo));
	}

	public get homeAddress(): Observable<HomeAddress> {
		return this.store.pipe(select(fromRoot.selectedGuestHomeAddress));
	}

	public get travelDocsList(): Observable<TravelDocument[]> {
		return this.store.pipe(select(fromRoot.selectedGuestTravelDocsList));
	}

	public get immigrationInfo(): Observable<ImmigrationInformation> {
		return this.store.pipe(select(fromRoot.selectedGuestImmigrationInfo));
	}

	public get contactInfo(): Observable<ContactInformation> {
		return this.store.pipe(select(fromRoot.selectedGuestContactInfo));
	}

	public get emergencyContactInfo(): Observable<EmergencyContactInformation> {
		return this.store.pipe(select(fromRoot.selectedGuestEmergencyContactInfo));
	}

	public selectedGuestId(): Observable<number> {
		return this.store.pipe(select(fromRoot.selectedGuestId));
	}

	public get error() {
		return this._state.error && Object.keys(this._state.error).length ? this._state.error : null;
	}

	public get snapshot(): State {
		return this._state;
	}

	public get countries$(): Observable<any[]> {
		return this.store.pipe(
			select(fromRoot.getCountries),
			map(({ countries }) => countries)
		);
	}

	public getGuestDetails(guestId?: number): GuestDetails | null {
		const selectedGuestId = !guestId ? this.snapshot.selectedGuestId : guestId;

		if (!selectedGuestId || !this.snapshot.guestDetails) {
			return null;
		}

		return this.snapshot.guestDetails[selectedGuestId];
	}

	public loaded(): Observable<boolean> {
		return this.store.pipe(
			select(fromRoot.guestDetailsIsLoading),
			filter(loading => !loading)
		);
	}

  public getGuestDetailsNotSelected(): GuestDetails[] {
    const guestsFromSnapshot = this.snapshot.guestDetails;
    this.selectedGuestId().pipe( take(1) ).subscribe(id => this.currentGuestId = id);
    const guestsNotSelected: GuestDetails[] = Object.values(
      guestsFromSnapshot
    ).filter((guest) => Number(guest.guestId) !== this.currentGuestId);
    return guestsNotSelected;
  }
}
